import type { ReactNode } from "react";
import dayjs from "dayjs";
import { DatePicker, Input, Select, Switch } from "antd";

import type { ScheduleStatus } from "@api/types";
import {
  ANNUALLY as DISTRIBUTION_FREQUENCY_ANNUALLY,
  MONTHLY as DISTRIBUTION_FREQUENCY_MONTHLY,
  QUARTERLY as DISTRIBUTION_FREQUENCY_QUARTERLY,
  SEMI_ANNUALLY as DISTRIBUTION_FREQUENCY_SEMI_ANNUALLY,
} from "@api/services/transactions/shapes/DistributionFrequencyEnum";
import { ACTIVE as SCHEDULE_STATUS_ACTIVE } from "@api/services/transactions/shapes/ScheduleStatusEnum";

import type { Frequency } from "../../types";
import SubmitButton from "../SubmitButton";

const LABEL_START_DATE = "Start Date";
const LABEL_FREQUENCY = "Frequency";
const LABEL_ROUND_CENTS = "Round Cents";

const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: DISTRIBUTION_FREQUENCY_MONTHLY, label: "Monthly" },
  { value: DISTRIBUTION_FREQUENCY_QUARTERLY, label: "Quarterly" },
  { value: DISTRIBUTION_FREQUENCY_SEMI_ANNUALLY, label: "Semi-Annually" },
  { value: DISTRIBUTION_FREQUENCY_ANNUALLY, label: "Annually" },
];

type FormField = {
  key: string;
  itemProps: Record<string, unknown>;
  children: ReactNode;
};

type Props = {
  isUpdate: boolean;
  isLoading: boolean;
  onDelete: () => void;
  scheduleStatus: ScheduleStatus;
  onEnable: () => void;
  onDisable: () => void;
};

const getFormFields = ({
  isUpdate,
  isLoading,
  onDelete,
  scheduleStatus,
  onEnable,
  onDisable,
}: Props): FormField[] => [
  {
    key: "projectId",
    itemProps: { name: "projectId", hidden: true },
    children: <Input />,
  },
  {
    key: "status",
    itemProps: { name: "status", hidden: true, ...(isUpdate ? {} : { initialValue: SCHEDULE_STATUS_ACTIVE }) },
    children: <Input />,
  },
  {
    key: "name",
    itemProps: { name: "name", hidden: true },
    children: <Input />,
  },
  {
    key: "transactionType",
    itemProps: { name: "transactionType", hidden: true },
    children: <Input />,
  },
  {
    key: "nextDate",
    itemProps: { name: "nextDate", hidden: true },
    children: <Input />,
  },
  {
    key: "frequency",
    itemProps: {
      name: "distributionFrequency",
      label: LABEL_FREQUENCY,
      rules: [{ required: true, message: `${LABEL_FREQUENCY} is required` }],
    },
    children: (
      <Select
        style={{ width: 140 }}
        options={FREQUENCY_OPTIONS}
        placeholder={LABEL_FREQUENCY}
      />
    ),
  },
  {
    key: "startDate",
    itemProps: {
      name: "startDate",
      label: LABEL_START_DATE,
      rules: [{ required: true, message: `${LABEL_START_DATE} is required` }],
      normalize: (value: dayjs.Dayjs | null) => value ? value.toISOString() : value,
      getValueProps: (value: string | null) => ({ value: value ? dayjs(value) : value }),
    },
    children: (
      <DatePicker
        picker="date"
        format="MM/DD/YYYY"
        placeholder={LABEL_START_DATE}
        allowClear={false}
      />
    ),
  },
  {
    key: "roundCents",
    itemProps: {
      name: "shouldRoundCents",
      label: LABEL_ROUND_CENTS,
      valuePropName: "checked",
    },
    children: <Switch />,
  },
  {
    key: "submit",
    itemProps: { style: { margin: 0 } },
    children: (
      <SubmitButton
        isUpdate={isUpdate}
        isLoading={isLoading}
        onDelete={onDelete}
        scheduleStatus={scheduleStatus}
        onEnable={onEnable}
        onDisable={onDisable}
      />
    ),
  },
];

export default getFormFields;
